import {
  INVALID_GCP_CONNECTOR_NAME,
  isGcpServiceAccountId,
  toGcpConnectorServiceAccount,
} from "@p0security/integrations-jira-ai-shared/connector-hosting";
import {
  isGcpProjectId,
  isGcpRegion,
} from "@p0security/integrations-jira-ai-shared/gcp-hosting";
import {
  INVALID_ITEM_ID,
  SITE_ID_MAX_LENGTH,
  isItemId,
} from "@p0security/integrations-jira-ai-shared/item-id";
import {
  isGcpSecretName,
  toPoolSecretPrefix,
  toTokenSecretPrefix,
} from "@p0security/integrations-jira-ai-shared/secret-name";
import type {
  JiraAiConnectorConfig,
  JiraAiSiteConfig,
} from "@p0security/integrations-jira-ai-shared/types";
import { tfId } from "@p0security/integrations-vaulted-credential-shared/iac";
import { ValidationError } from "@p0security/shared/types/error";
import { Typography } from "@p0security/ui";
import type { ReactNode } from "react";

// The Terraform each setup page shows, generated from the item it's for.
//
// Every value written into a snippet passes a rule first, so that none can
// end its string, start an interpolation or end the comment it's on.

/** One Terraform snippet on a setup page, and the paragraph above it. */
export type IacCommand = Readonly<{ command: string; header: ReactNode }>;

type GcpHosting = JiraAiConnectorConfig["hosting"];

const { Paragraph, Text } = Typography;

/** The prefix of every Terraform name a connector item's Terraform declares.
 * The id must already pass `isItemId`: it may start with a digit, which the
 * prefix makes a valid Terraform name. */
const toConnectorTfName = (id: string) => `p0_jira_ai_connector_${tfId(id)}`;

/** The prefix of every Terraform name a site item's Terraform declares.
 * Distinct from a connector's, so a connector and a site with the same id
 * don't collide. */
const toSiteTfName = (id: string) => `p0_jira_ai_site_${tfId(id)}`;

// Copied from Harbor's connector Terraform, in
// packages/integrations/harbor/frontend/src/iac.tsx, and kept close to it:
// `UNPUBLISHED_DIGEST`, `IMAGE_PIN_PATTERN`, `matches` (here `isMatch`),
// `validateField`, `validateItemId`, `validateImagePin` (here
// `parseImagePin`), `REQUIRED_PROVIDERS`,
// `renderImageDigestCheck`, `toServiceAccountId`, `toGcpConnector` and
// `renderGcpConnectorTerraform`. Changed on purpose: Google Cloud only, no
// email domain pattern, and jira-ai's names and image.
// TODO(INT-432): share them, and drop these copies.

// The all-zero digest that stands in for an image before it's published.
const UNPUBLISHED_DIGEST = `sha256:${"0".repeat(64)}`;

// The connector build the Terraform deploys, as `<tag>@sha256:<digest>`: a
// p0-security-perimeter main build's sha-<commit> tag on Docker Hub, and that
// tag's digest. Find the digest with
// `docker buildx imagetools inspect p0security/p0-connector-jira-ai-gcloud:<tag>`.
const GCP_IMAGE_PIN =
  "sha-36e7fc4@sha256:4345ed58e3be095fe1ac9b0205d97be681b75ca8dd6ebc049752913d52f8440d";

/** P0's Jira (AI) connector image for Cloud Run, on Docker Hub. */
const CLOUD_RUN_IMAGE = "p0security/p0-connector-jira-ai-gcloud";

// Docker caps a tag at 128 characters.
const IMAGE_PIN_PATTERN = /^\w[\w.-]{0,90}@sha256:[a-f0-9]{64}$/;

// P0's own service account comes from the Google Cloud integration's
// install. This checks only that it's an email whose characters are safe in
// the Terraform.
const INVOKER_EMAIL_PATTERN = /^[\w.+-]+@[\w.-]+$/;

// A custom role's id: letters, digits, `_` and `.`, up to 64 characters.
const CUSTOM_ROLE_ID_PATTERN = /^[A-Za-z0-9_.]{3,64}$/;

/** What a connector's refusal tells the customer to do. A connector's fields
 * are fixed when it's created, so there's nothing to correct in place. */
const REINSTALL_CONNECTOR = "Delete this connector and add it again";

/** What a site's refusal tells the customer to do. */
const REINSTALL_SITE = "Delete this site and add it again";

/** Whether `value` is a string that `pattern` matches. */
const isMatch = (pattern: RegExp, value: unknown): value is string =>
  typeof value === "string" && pattern.test(value);

/** `value`, if it's a string that passes `test`. Otherwise throws a
 * {@link ValidationError} for `field` with `message`. Neither the error's
 * state nor its message holds the value.
 *
 * @param args.exact Checks the value as it stands, so that padding is
 *   refused. For a value P0 also sends the connector as it stands.
 */
const validateField = (args: {
  exact?: boolean;
  field: string;
  message: string;
  test: (value: string) => boolean;
  value: unknown;
}) => {
  const { exact = false, field, message, test, value } = args;
  const checked =
    typeof value !== "string" ? undefined : exact ? value : value.trim();
  if (checked === undefined || !test(checked)) {
    throw new ValidationError(field, {}, message);
  }
  return checked;
};

/** Throws a {@link ValidationError} unless `id` can be part of a Terraform
 * name. */
const validateItemId = (args: { id: string; reinstall: string }) => {
  const { id, reinstall } = args;
  if (!isItemId(id)) {
    throw new ValidationError(
      "id",
      {},
      `This ID can't name Terraform resources. ${reinstall} with another ID. ${INVALID_ITEM_ID}`
    );
  }
};

/** The tag and digest of `imagePin`.
 *
 * @throws ValidationError if it's the placeholder of an image that isn't
 * published yet, which the customer has to wait out
 * @throws Error if it isn't a pin at all, which is P0's mistake
 */
const parseImagePin = (imagePin: unknown) => {
  if (!isMatch(IMAGE_PIN_PATTERN, imagePin)) {
    throw new Error("The connector image must be <tag>@sha256:<digest>.");
  }
  const [imageTag, imageDigest] = imagePin.split("@");
  if (imageDigest === UNPUBLISHED_DIGEST) {
    throw new ValidationError(
      "imagePin",
      {},
      "P0 hasn't published the Jira connector image yet, so it can't give you the connector's Terraform."
    );
  }
  return { imageDigest, imageTag };
};

/** The `required_providers` block of a connector's Terraform. 3.3.0 is the
 * first release of the docker provider with `disable_docker_daemon_check`. */
const REQUIRED_PROVIDERS = `terraform {
  # If your configuration already has a required_providers block, add docker to
  # it, at 3.3 or later, and delete this block.
  required_providers {
    docker = {
      source  = "kreuzwerker/docker"
      version = ">= 3.3, < 5.0"
    }
  }
}`;

/** The data source that reads a connector image's digest from Docker Hub, and
 * fails the plan unless it's the pinned one. */
const renderImageDigestCheck = (
  tfName: string
) => `# Stops the plan if the tag has been moved to another image since P0 pinned
# it, so that only the image P0 pinned is deployed.
data "docker_registry_image" "${tfName}" {
  provider = docker.${tfName}

  name = "${CLOUD_RUN_IMAGE}:\${local.${tfName}.image_tag}"

  lifecycle {
    postcondition {
      condition     = self.sha256_digest == local.${tfName}.image_digest
      error_message = "The connector image's tag doesn't resolve to the digest pinned here. Copy the current Terraform from P0's Jira installer, or contact support@p0.dev."
    }
  }
}`;

/**
 * The ID of the service account a connector runs as: the account P0 stored
 * for the connector, or, before P0 stores one, the connector's name.
 *
 * @throws ValidationError if the stored account isn't a service account, with
 * an ID a connector's can have, in the connector's project
 */
const toServiceAccountId = (args: {
  connectorName: string;
  projectId: string;
  storedServiceAccount: unknown;
  unusable: Readonly<{ field: string; message: string }>;
}) => {
  const { connectorName, projectId, storedServiceAccount, unusable } = args;
  if (storedServiceAccount === undefined || storedServiceAccount === "") {
    return connectorName;
  }
  const domain = `@${projectId}.iam.gserviceaccount.com`;
  const accountId =
    typeof storedServiceAccount === "string" &&
    storedServiceAccount.endsWith(domain)
      ? storedServiceAccount.slice(0, -domain.length)
      : undefined;
  if (
    accountId === undefined ||
    accountId !== accountId.trim() ||
    !isGcpServiceAccountId(accountId)
  ) {
    throw new ValidationError(unusable.field, {}, unusable.message);
  }
  return accountId;
};

/** The arguments to {@link renderGcpConnector}. */
export type GcpConnectorArgs = Readonly<{
  hosting: GcpHosting;
  /** The connector item's id */
  id: string;
  /** The connector image to deploy, as `<tag>@sha256:<digest>`. Defaults to
   * the build P0 pins. */
  imagePin?: string;
  /** P0's own service account, or "" before the Google Cloud integration is
   * installed */
  invokerEmail: string;
}>;

/** A connector on Google Cloud, as its Terraform deploys it. Each value has
 * passed the rule that makes it safe in the Terraform. */
type GcpConnector = Readonly<{
  connectorName: string;
  connectorRegion: string;
  id: string;
  imageDigest: string;
  imageTag: string;
  invokerEmail: string;
  projectId: string;
  serviceAccountId: string;
}>;

/**
 * Resolves the connector that one connector item's Terraform deploys.
 *
 * @throws ValidationError, whose message is for the customer, for the first
 * input the connector can't be deployed with
 * @throws Error for P0's own mistakes: an image pin that isn't one, or an
 * invoker that isn't an email
 */
const toGcpConnector = (args: GcpConnectorArgs): GcpConnector => {
  const { hosting, id, imagePin = GCP_IMAGE_PIN, invokerEmail } = args;
  validateItemId({ id, reinstall: REINSTALL_CONNECTOR });
  const projectId = validateField({
    field: "hosting.projectId",
    message: `This connector's project isn't a Google Cloud project ID: 6 to 30 lowercase letters, digits and hyphens, not a project's number or name. ${REINSTALL_CONNECTOR}.`,
    test: isGcpProjectId,
    value: hosting.projectId,
  });
  const connectorName = validateField({
    field: "hosting.connectorName",
    message: `This connector's name can't name a Cloud Run service and its service account. ${REINSTALL_CONNECTOR} with another name. ${INVALID_GCP_CONNECTOR_NAME}`,
    test: isGcpServiceAccountId,
    value: hosting.connectorName,
  });
  const connectorRegion = validateField({
    field: "hosting.connectorRegion",
    message: `This connector's region isn't a Google Cloud region, such as us-west1. ${REINSTALL_CONNECTOR}.`,
    test: isGcpRegion,
    value: hosting.connectorRegion,
  });
  const serviceAccountId = toServiceAccountId({
    connectorName,
    projectId,
    storedServiceAccount: hosting.connectorServiceAccount,
    unusable: {
      field: "hosting.connectorServiceAccount",
      message: `The service account P0 holds for this connector isn't one in the connector's project. ${REINSTALL_CONNECTOR}.`,
    },
  });
  if (!invokerEmail) {
    throw new ValidationError(
      "invokerEmail",
      {},
      "Install P0's Google Cloud IAM management first, then return here. The connector accepts calls only from P0's Google Cloud service account, which that install provides."
    );
  }
  if (!isMatch(INVOKER_EMAIL_PATTERN, invokerEmail)) {
    throw new Error(
      "P0's Google Cloud integration holds an invoker that isn't an email."
    );
  }
  const { imageDigest, imageTag } = parseImagePin(imagePin);
  return {
    connectorName,
    connectorRegion,
    id,
    imageDigest,
    imageTag,
    invokerEmail,
    projectId,
    serviceAccountId,
  };
};

/**
 * Renders the Terraform that deploys one connector item's connector on Cloud
 * Run, running as the service account P0 holds for it. The account has no
 * role here: each site that uses the connector grants it what it needs in the
 * site's vault project, from its own Terraform. P0's service account can
 * invoke the service and read it.
 *
 * Every Terraform name carries the item's id, so several connectors, and the
 * sites' grants, can share a configuration.
 */
const renderGcpConnectorTerraform = (connector: GcpConnector): string => {
  const {
    connectorName,
    connectorRegion,
    id: itemId,
    imageDigest,
    imageTag,
    invokerEmail,
    projectId,
    serviceAccountId,
  } = connector;
  const tfName = toConnectorTfName(itemId);

  return `# P0's Jira connector ${itemId}, on Google Cloud Run.
#
# It needs Terraform's google provider at 6.0 or later, and the Service Usage
# API enabled in project ${projectId},
# which lets Terraform enable the IAM and Cloud Run APIs. If the first apply
# fails right after it enables them, apply again: a newly enabled API can take
# a few minutes to answer.
#
# Cloud Run pulls P0's connector image from Docker Hub. Terraform reads the
# image's digest from Docker Hub on every plan, so the machine that runs
# Terraform has to reach it. It needs no Docker.
#
# Other connectors can share this configuration, but each needs its own
# connector name: the connector's service is named after it.
#
# Destroying this deletes the connector's service account. Destroy the grants
# of each Jira site that uses this connector too.

terraform {
  # The image's digest check needs Terraform 1.2.
  required_version = ">= 1.2"
}

${REQUIRED_PROVIDERS}

locals {
  ${tfName} = {
    # Where P0 invokes the connector. P0's installer has the same values.
    project_id       = "${projectId}"
    connector_name   = "${connectorName}"
    connector_region = "${connectorRegion}"
    # The ID of the service account the connector runs as.
    service_account_id = "${serviceAccountId}"
    # P0's own service account. The connector accepts calls from it alone.
    invoker_email = "${invokerEmail}"
    # P0's connector image: its tag on Docker Hub, and the digest that tag must
    # resolve to.
    image_tag    = "${imageTag}"
    image_digest = "${imageDigest}"
  }
}

# Reads the image's digest from Docker Hub. It never talks to a Docker daemon,
# so none has to be running.
provider "docker" {
  alias                       = "${tfName}"
  disable_docker_daemon_check = true
}

${renderImageDigestCheck(tfName)}

# The IAM, Cloud Run and Secret Manager APIs. Never disabled on destroy: other
# services in the project may use them.
resource "google_project_service" "${tfName}" {
  for_each = toset(["iam.googleapis.com", "run.googleapis.com", "secretmanager.googleapis.com"])

  project            = local.${tfName}.project_id
  service            = each.key
  disable_on_destroy = false
}

# The identity the connector runs as. It has no role here: each Jira site that
# uses the connector grants it what it needs, from its own Terraform.
#
# Don't destroy and recreate it. Google treats a recreated account as a new
# one, even under the same name: it doesn't get the old account's roles.
resource "google_service_account" "${tfName}" {
  project      = local.${tfName}.project_id
  account_id   = local.${tfName}.service_account_id
  display_name = "P0 Jira connector ${itemId}"

  depends_on = [google_project_service.${tfName}]
}

resource "google_cloud_run_v2_service" "${tfName}" {
  project             = local.${tfName}.project_id
  name                = local.${tfName}.connector_name
  location            = local.${tfName}.connector_region
  deletion_protection = false
  # P0 calls the connector from outside Google Cloud, so IAM controls access,
  # not the caller's network.
  ingress = "INGRESS_TRAFFIC_ALL"

  template {
    service_account = google_service_account.${tfName}.email

    containers {
      image = "docker.io/${CLOUD_RUN_IMAGE}:\${local.${tfName}.image_tag}@\${data.docker_registry_image.${tfName}.sha256_digest}"

      # The connector checks every caller's token against this, and refuses
      # every call while it's unset.
      env {
        name  = "INVOKER_SA_EMAIL"
        value = local.${tfName}.invoker_email
      }
    }
  }

  depends_on = [google_project_service.${tfName}]
}

# P0 invokes the connector as its own service account. Nothing here lets
# anyone else invoke it.
resource "google_cloud_run_v2_service_iam_member" "${tfName}_invoker" {
  project  = local.${tfName}.project_id
  location = google_cloud_run_v2_service.${tfName}.location
  name     = google_cloud_run_v2_service.${tfName}.name
  role     = "roles/run.invoker"
  member   = "serviceAccount:\${local.${tfName}.invoker_email}"
}

# P0 reads the service to get its invocation URL when it installs.
resource "google_cloud_run_v2_service_iam_member" "${tfName}_viewer" {
  project  = local.${tfName}.project_id
  location = google_cloud_run_v2_service.${tfName}.location
  name     = google_cloud_run_v2_service.${tfName}.name
  role     = "roles/run.viewer"
  member   = "serviceAccount:\${local.${tfName}.invoker_email}"
}

output "${tfName}" {
  value = {
    project_id       = local.${tfName}.project_id
    connector_name   = google_cloud_run_v2_service.${tfName}.name
    connector_region = google_cloud_run_v2_service.${tfName}.location
  }

  depends_on = [
    google_cloud_run_v2_service_iam_member.${tfName}_invoker,
    google_cloud_run_v2_service_iam_member.${tfName}_viewer,
  ]
}
`;
};

/** The connector on Cloud Run: the APIs it needs, the service account it
 * runs as, the service, and P0's permission to invoke and read it.
 *
 * @throws ValidationError, whose message is for the customer
 * @throws Error for P0's own mistakes
 */
export const renderGcpConnector = (args: GcpConnectorArgs): IacCommand => {
  const connector = toGcpConnector(args);
  return {
    command: renderGcpConnectorTerraform(connector),
    header: (
      <Paragraph>
        Deploys the connector as the Cloud Run service{" "}
        <Text code>{connector.connectorName}</Text> in Google Cloud project{" "}
        {connector.projectId}, in {connector.connectorRegion}, running as the
        service account <Text code>{connector.serviceAccountId}</Text>, and lets
        P0 invoke it. The project needs the Service Usage API enabled, and the
        machine that runs Terraform has to reach Docker Hub.
      </Paragraph>
    ),
  };
};

/** The arguments to {@link renderSiteGrants}. */
export type SiteGrantsArgs = Readonly<{
  /** The id of the connector item the site names */
  connectorId: string;
  /** Where that connector runs */
  hosting: GcpHosting;
  /** The site item's id */
  id: string;
  site: Pick<JiraAiSiteConfig, "adminSecretName" | "vault">;
}>;

/** A site's grants, as its Terraform declares them. Each value has passed
 * the rule that makes it safe in the Terraform. */
type SiteGrants = Readonly<{
  adminSecretName: string;
  connectorServiceAccount: string;
  id: string;
  poolSecretPrefix: string;
  /** The ids of the two custom roles the site creates in the vault project */
  roleIds: Readonly<{ create: string; manage: string }>;
  tokenSecretPrefix: string;
  vaultProjectId: string;
}>;

/** The ids of a site's custom roles. Custom role ids are per project, so each
 * carries the site's id, with `-` mapped to `_`. */
const toRoleIds = (id: string) => ({
  create: `p0JiraAi_${tfId(id)}_create`,
  manage: `p0JiraAi_${tfId(id)}_manage`,
});

/**
 * Resolves the grants one site item's Terraform declares.
 *
 * @throws ValidationError, whose message is for the customer, for the first
 * input the grants can't be declared with
 */
const toSiteGrants = (args: SiteGrantsArgs): SiteGrants => {
  const { connectorId, hosting, id, site } = args;
  validateItemId({ id, reinstall: REINSTALL_SITE });
  const roleIds = toRoleIds(id);
  if (
    !CUSTOM_ROLE_ID_PATTERN.test(roleIds.create) ||
    !CUSTOM_ROLE_ID_PATTERN.test(roleIds.manage)
  ) {
    throw new ValidationError(
      "id",
      {},
      `This site's ID is too long to name its custom roles. ${REINSTALL_SITE} with an ID of at most ${SITE_ID_MAX_LENGTH} characters.`
    );
  }
  if (site.vault.type !== "gcp-sm") {
    throw new ValidationError(
      "vault",
      {},
      `The Jira connector reads secrets only in Google Secret Manager. ${REINSTALL_SITE} with Google Secret Manager.`
    );
  }
  const vaultProjectId = validateField({
    exact: true,
    field: "vault.install",
    message: `This site's secrets project isn't a Google Cloud project ID. ${REINSTALL_SITE}.`,
    test: isGcpProjectId,
    value: site.vault.install,
  });
  const adminSecretName = validateField({
    exact: true,
    field: "adminSecretName",
    message: `This site's admin secret name can't name a secret in Google Secret Manager. ${REINSTALL_SITE}.`,
    test: isGcpSecretName,
    value: site.adminSecretName,
  });
  const unknownServiceAccount = `P0 can't tell which service account this site's connector runs as. Delete this site, then delete the connector and add both again.`;
  if (!isItemId(connectorId)) {
    throw new ValidationError("connector", {}, unknownServiceAccount);
  }
  const projectId = validateField({
    field: "connector",
    message: unknownServiceAccount,
    test: isGcpProjectId,
    value: hosting.projectId,
  });
  const connectorName = validateField({
    field: "connector",
    message: unknownServiceAccount,
    test: isGcpServiceAccountId,
    value: hosting.connectorName,
  });
  const serviceAccountId = toServiceAccountId({
    connectorName,
    projectId,
    storedServiceAccount: hosting.connectorServiceAccount,
    unusable: { field: "connector", message: unknownServiceAccount },
  });
  return {
    adminSecretName,
    connectorServiceAccount: toGcpConnectorServiceAccount({
      connectorName: serviceAccountId,
      projectId,
    }),
    id,
    poolSecretPrefix: toPoolSecretPrefix(id),
    roleIds,
    tokenSecretPrefix: toTokenSecretPrefix(id),
    vaultProjectId,
  };
};

/**
 * Renders the Terraform that gives a site's connector what it needs in the
 * site's vault project, and nothing else:
 *
 * - Read on the admin client's secret, that secret alone. Copied from
 *   Harbor's `renderGcpReadGrantTerraform`.
 * - Read on the site's agent account secrets, as a project binding
 *   conditioned on their prefix.
 * - `secretmanager.secrets.create` on the project, and delete, get and
 *   add-version on the site's token secrets, by their prefix. Copied from
 *   `tokenSecretTf` in packages/integrations/vaulted-credential/shared/src/iac.ts,
 *   which isn't exported.
 *
 * Every Terraform name and custom role id carries the site's id, so the
 * grants of several sites can share a configuration and a vault project.
 */
const renderSiteGrantsTerraform = (grants: SiteGrants): string => {
  const {
    adminSecretName,
    connectorServiceAccount,
    id: itemId,
    poolSecretPrefix: poolPrefix,
    roleIds,
    tokenSecretPrefix: tokenPrefix,
    vaultProjectId,
  } = grants;
  const tfName = toSiteTfName(itemId);

  return `# Lets P0's Jira connector use the secrets of the Jira site ${itemId}, in
# its secrets project, and no others.
#
# The admin client's secret has to exist already. This doesn't create it, and
# never reads or holds a secret's value. The connector's service account has
# to exist too, so apply the connector's Terraform first. Apply this again
# after the connector's service account is recreated.

locals {
  ${tfName} = {
    vault_project_id = "${vaultProjectId}"
    admin_secret     = "${adminSecretName}"
    # Each pool account's API key secret starts with this.
    pool_secret_prefix = "${poolPrefix}"
    # Each request's token secret starts with this.
    token_secret_prefix = "${tokenPrefix}"
    # The service account the site's connector runs as.
    connector_service_account = "${connectorServiceAccount}"
  }
}

# A condition matches on the resource name, which carries the project number
# and not the project id.
data "google_project" "${tfName}" {
  project_id = local.${tfName}.vault_project_id
}

# The connector reads the admin client's secret. The accessor role reads a
# value. The viewer role reads the version's metadata, which the connector
# also needs, and can change nothing.
resource "google_secret_manager_secret_iam_member" "${tfName}_admin_accessor" {
  project   = local.${tfName}.vault_project_id
  secret_id = local.${tfName}.admin_secret
  role      = "roles/secretmanager.secretAccessor"
  member    = "serviceAccount:\${local.${tfName}.connector_service_account}"
}

resource "google_secret_manager_secret_iam_member" "${tfName}_admin_viewer" {
  project   = local.${tfName}.vault_project_id
  secret_id = local.${tfName}.admin_secret
  role      = "roles/secretmanager.viewer"
  member    = "serviceAccount:\${local.${tfName}.connector_service_account}"
}

# The connector reads each pool account's API key on this site, and no
# other site's: a site's ID holds no underscore, so the underscore after it
# ends the prefix.
resource "google_project_iam_member" "${tfName}_pool_accessor" {
  project = local.${tfName}.vault_project_id
  role    = "roles/secretmanager.secretAccessor"
  member  = "serviceAccount:\${local.${tfName}.connector_service_account}"

  condition {
    title      = "P0 Jira site ${itemId} agent account secrets"
    expression = "resource.name.startsWith('projects/\${data.google_project.${tfName}.number}/secrets/\${local.${tfName}.pool_secret_prefix}')"
  }
}

resource "google_project_iam_member" "${tfName}_pool_viewer" {
  project = local.${tfName}.vault_project_id
  role    = "roles/secretmanager.viewer"
  member  = "serviceAccount:\${local.${tfName}.connector_service_account}"

  condition {
    title      = "P0 Jira site ${itemId} agent account secrets"
    expression = "resource.name.startsWith('projects/\${data.google_project.${tfName}.number}/secrets/\${local.${tfName}.pool_secret_prefix}')"
  }
}

# Google checks secretmanager.secrets.create on the project, so this binding
# cannot carry the condition that the management binding below does.
resource "google_project_iam_custom_role" "${tfName}_token_create" {
  project     = local.${tfName}.vault_project_id
  role_id     = "${roleIds.create}"
  title       = "P0 Jira site ${itemId} token secret creation"
  permissions = ["secretmanager.secrets.create"]
}

resource "google_project_iam_member" "${tfName}_token_create" {
  project = local.${tfName}.vault_project_id
  role    = google_project_iam_custom_role.${tfName}_token_create.name
  member  = "serviceAccount:\${local.${tfName}.connector_service_account}"
}

resource "google_project_iam_custom_role" "${tfName}_token_manage" {
  project = local.${tfName}.vault_project_id
  role_id = "${roleIds.manage}"
  title   = "P0 Jira site ${itemId} token secret management"
  permissions = [
    "secretmanager.secrets.delete",
    "secretmanager.secrets.get",
    "secretmanager.versions.add",
  ]
}

resource "google_project_iam_member" "${tfName}_token_manage" {
  project = local.${tfName}.vault_project_id
  role    = google_project_iam_custom_role.${tfName}_token_manage.name
  member  = "serviceAccount:\${local.${tfName}.connector_service_account}"

  condition {
    title      = "P0 Jira site ${itemId} token secrets"
    expression = "resource.name.startsWith('projects/\${data.google_project.${tfName}.number}/secrets/\${local.${tfName}.token_secret_prefix}')"
  }
}
`;
};

/** A site's grants in its vault project, for its connector's service
 * account.
 *
 * @throws ValidationError, whose message is for the customer
 */
export const renderSiteGrants = (args: SiteGrantsArgs): IacCommand => {
  const grants = toSiteGrants(args);
  return {
    command: renderSiteGrantsTerraform(grants),
    header: (
      <Paragraph>
        Lets the connector&apos;s service account{" "}
        <Text code>{grants.connectorServiceAccount}</Text> read the admin
        client&apos;s secret <Text code>{grants.adminSecretName}</Text> and this
        site&apos;s agent account secrets, and create and delete this
        site&apos;s token secrets, in Google Cloud project{" "}
        <Text code>{grants.vaultProjectId}</Text>. Apply it after the
        connector&apos;s Terraform. The admin secret has to exist already.
      </Paragraph>
    ),
  };
};
